// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * chatStore.videoExpiration (AI-3 Rework v2 — Lane D decomposition).
 *
 * Decomposition seam for the video-expiration helpers that lived
 * inline in `chatStore.ts`.  The three functions form a cohesive
 * slice:
 *
 *   - ``extractVideoFileIds(messages)``
 *   - ``expireConversationVideos(sessionId, fileIds)``
 *   - ``getSessionMessagesForVideoExpiration(state, sessionId)``
 *
 * They walk the UI blocks of a chat session, identify Manim-style
 * video artifacts, and trigger a fire-and-forget expiration call
 * when the user navigates away.  This module pulls the slice out
 * of the store so the store no longer owns a 35-line "video
 * lifecycle" inline block.
 *
 * Behavioural parity: the original functions operated on the
 * ``ChatState`` shape directly; this module takes a narrowed
 * ``Pick<ChatState, 'currentSession' | 'messages' | 'messagesBySession'>``
 * so the slice is decoupled from the rest of the store's state.
 */

import { api } from '@api/client';
import type { Message, Session } from '@/types';

export interface VideoExpirationState {
  currentSession: Session | null;
  messages: Message[];
  messagesBySession: Record<string, Message[]>;
}

export function extractVideoFileIds(messages: Message[]): string[] {
  const fileIds: string[] = [];
  const urlPattern = /\/api\/v1\/files\/([^/\s]+)/;
  for (const msg of messages) {
    for (const block of msg.ui_blocks ?? []) {
      const payload = block.payload as unknown as Record<string, unknown> | undefined;
      const animation = payload?.animation as Record<string, unknown> | undefined;
      if (!animation) continue;
      const status = animation.status as string | undefined;
      if (status !== 'ready' && status !== 'completed') continue;
      // Prefer file_id, fall back to parsing video_url.
      if (typeof animation.file_id === 'string' && animation.file_id) {
        fileIds.push(animation.file_id);
      } else if (typeof animation.video_url === 'string') {
        const match = urlPattern.exec(animation.video_url);
        if (match) fileIds.push(match[1]);
      }
    }
  }
  return [...new Set(fileIds)];
}

/** Fire-and-forget: expire video files for a conversation being left. */
export function expireConversationVideos(sessionId: string, fileIds: string[]): void {
  if (!fileIds.length) return;
  api.expireVideos(sessionId, fileIds).catch(() => {});
}

export function getSessionMessagesForVideoExpiration(
  state: VideoExpirationState,
  sessionId: string,
): Message[] {
  if (state.currentSession?.session_id === sessionId) {
    return state.messages;
  }
  return state.messagesBySession[sessionId] ?? [];
}
